import { Head, router } from "@inertiajs/react"
import { Card, Flex, Heading, Table, TextField, Link, Badge, Code } from "@radix-ui/themes"
import { Plus, Search, FileUp, Check, X } from "lucide-react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import Pagination from "@/components/pagination"
import ProjectLayout from "@/layouts/project-layout"
import {
  newProjectCustomerPath,
  projectDashboardPath,
  projectCustomersPath,
  searchProjectCustomersPath,
  projectCustomerPath,
  newProjectImportPath,
  toggleSubscriptionProjectCustomerPath,
} from "@/routes"
import { CustomerCustomFieldSchema, CustomerSchema, ProjectSchema } from "@/schemas"
import { countKeys } from "@/lib/objects"

const PropsSchema = z.object({
  project: ProjectSchema,
  customers: z.array(CustomerSchema),
  custom_fields: z.array(CustomerCustomFieldSchema),
  current_page: z.number(),
  total_pages: z.number(),
  total_count: z.number(),
})

type Props = z.infer<typeof PropsSchema>

export default function CustomersIndex(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const {
    project,
    customers,
    custom_fields: customFields,
    current_page,
    total_pages,
    total_count,
  } = validatedProps

  const handlePageChange = (page: number) => {
    router.get(projectCustomersPath(project.slug), { page }, { preserveState: true })
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const email = event.currentTarget.value.trim()
      if (email) {
        router.post(searchProjectCustomersPath(project.slug), { email })
      }
    }
  }

  const handleToggleSubscription = (customerId: number, type: "campaigns" | "newsletters") => {
    router.patch(toggleSubscriptionProjectCustomerPath(project.slug, customerId), { type })
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Customers" },
      ]}
    >
      <Head title="Customers" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Customers ({total_count})
          </Heading>

          <Flex gap="3" align="center">
            <TextField.Root
              placeholder="find by email"
              onKeyDown={handleSearchKeyDown}
              style={{ minWidth: "200px" }}
            >
              <TextField.Slot>
                <Search className="h-4 w-4" />
              </TextField.Slot>
            </TextField.Root>

            <LinkButton href={newProjectImportPath(project.slug)} variant="outline">
              <FileUp className="h-4 w-4" />
              <span>Import</span>
            </LinkButton>

            <LinkButton href={newProjectCustomerPath(project.slug)} variant="solid">
              <Plus className="h-4 w-4" />
              <span>New customer</span>
            </LinkButton>
          </Flex>
        </Flex>

        <Flex gap="2" wrap="wrap">
          {customFields.map((field, i) => (
            <Badge key={i}>
              {field.field_name}: {field.field_type}
            </Badge>
          ))}
        </Flex>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Subscriptions</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Custom Fields</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Devices</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Events Count</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {customers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={7} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No customers found
                  </Table.Cell>
                </Table.Row>
              ) : (
                customers.map((customer) => (
                  <Table.Row key={customer.id}>
                    <Table.RowHeaderCell>
                      <Link href={projectCustomerPath(project.slug, customer.id)}>
                        {customer.email}
                      </Link>
                    </Table.RowHeaderCell>
                    <Table.Cell>
                      <Badge color={customer.source === "event" ? "purple" : "red"} variant="soft">
                        {customer.source}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap="1">
                        <Badge
                          color={customer.wants_campaigns ? "green" : "red"}
                          variant="soft"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleSubscription(customer.id, "campaigns")}
                        >
                          <Flex align="center" gap="1">
                            {customer.wants_campaigns ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                            Campaigns
                          </Flex>
                        </Badge>
                        <Badge
                          color={customer.wants_newsletters ? "green" : "red"}
                          variant="soft"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleSubscription(customer.id, "newsletters")}
                        >
                          <Flex align="center" gap="1">
                            {customer.wants_newsletters ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                            Newsletters
                          </Flex>
                        </Badge>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      {countKeys(customer.custom_fields) > 0 ? (
                        <Code
                          size="1"
                          style={{ display: "block", maxWidth: "300px", overflow: "auto" }}
                        >
                          {JSON.stringify(customer.custom_fields, null, 2)}
                        </Code>
                      ) : (
                        <span style={{ color: "var(--gray-11)", fontStyle: "italic" }}>
                          No custom fields
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>{customer.customer_devices_count}</Table.Cell>
                    <Table.Cell>{customer.events_count}</Table.Cell>
                    <Table.Cell>
                      {new Date(customer.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>

        <Pagination
          currentPage={current_page}
          totalPages={total_pages}
          onPageChange={handlePageChange}
        />
      </Flex>
    </ProjectLayout>
  )
}
